import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import apiClient from "../../Services/apiClient";
import { useNavigate } from "react-router-dom";

function GroupPage() {
  const { groupId } = useParams();
  const [categories, setCategories] = useState([]);   // renamed for clarity
  const [loading, setLoading] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [formData, setFormData] = useState({});
  const navigate = useNavigate();

  // Get user role once
  const userRole = localStorage.getItem('role') || 'USER';

  // Fetch services when groupId changes
  useEffect(() => {
    if (!groupId) return;

    const token = localStorage.getItem("token");
    setLoading(true);

    apiClient
      .get(`services/group/${groupId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        setCategories(res.data || []);
      })
      .catch((err) => {
        console.error(err);
        setCategories([]);
      })
      .finally(() => setLoading(false));
  }, [groupId]);

  // Function to get correct price for a service based on role
  const getDisplayPrice = (service) => {
    if (!service?.prices || service.prices.length === 0) return 0;

    if (userRole === 'AGENT') {
      const agentPrice = service.prices.find(p => p.userType === 'AGENT');
      if (agentPrice) return agentPrice.price;
    }

    // For ALL other roles (USER or anything else) → show USER price
    const userPrice = service.prices.find(p => p.userType === 'USER');
    if (userPrice) return userPrice.price;

    // Final fallback: show minimum price
    return Math.min(...service.prices.map(p => p.price));
  };

  const handleApply = (serviceId) => {
    navigate(`/service/${serviceId}/apply`);
  };

  const handleInputChange = (e) => {
    const { name, value, files, type } = e.target;
    if (type === "file") {
      setFormData({ ...formData, [name]: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("token");

    try {
      const formPayload = new FormData();
      Object.keys(formData).forEach((key) => {
        formPayload.append(key, formData[key]);
      });

      await apiClient.post(
        `services/${selectedService.id}/apply`,
        formPayload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      alert("Application submitted successfully!");
      setSelectedService(null);
      setFormData({});
    } catch (err) {
      console.error(err);
      alert("Failed to submit application.");
    }
  };

  if (loading) {
    return (
      <div className="content-page">
        <div className="content">
          <div className="container-fluid">
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading services...</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content-page">
      <div className="content">
        <div className="container-fluid">
          {/* Page Title */}
          <div className="row">
            <div className="col-12">
              <div className="page-title-box">
                <div className="page-title-right">
                  <ol className="breadcrumb m-0">
                    <li className="breadcrumb-item">
                      <Link to="/dashboard">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item active">All Services</li>
                  </ol>
                </div>
                <h4 className="page-title">Services</h4>
              </div>
            </div>
          </div>

          {/* Services List */}
          {categories.length > 0 ? (
            categories.map((category) => (
              <div key={category.categoryId} className="row mb-4">
                <div className="col-12">
                  <h4 className="mb-3 border-bottom pb-2">
                    {category.categoryName}
                  </h4>
                </div>

                {category.services.map((service) => {
                  const price = getDisplayPrice(service);

                  return (
                    <div
                      className="col-xl-3 col-lg-4 col-md-6 mb-4"
                      key={service.id}
                    >
                      <div className="card h-100 shadow-sm border-0">

                        {/* Image Container */}
                        <div
                          className="position-relative overflow-hidden"
                          style={{
                            height: "180px",
                            backgroundColor: "#f1f3f5"
                          }}
                        >
                          {service.imageUrl ? (
                            <img
                              src={service.imageUrl}
                              alt={service.name}
                              className="w-100 h-100"
                              style={{ objectFit: "cover", objectPosition: "center" }}
                            />
                          ) : (
                            <div className="d-flex align-items-center justify-content-center h-100 text-muted"
                                 style={{ fontSize: "3rem", background: "linear-gradient(135deg, #e9ecef, #dee2e6)" }}>
                              <i className="mdi mdi-image-outline"></i>
                            </div>
                          )}
                        </div>

                        {/* Card Body */}
                        <div className="card-body d-flex flex-column text-center">
                          <h5 className="card-title mb-3">{service.name}</h5>

                          <p className="card-text text-muted small">
                            Charges : ₹{price}
                          </p>

                          <div className="mt-auto">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => handleApply(service.id)}
                            >
                              Apply Now
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          ) : (
            <div className="row">
              <div className="col-12">
                <p className="text-center text-muted py-5">
                  No services available in this group.
                </p>
              </div>
            </div>
          )}

          {/* Dynamic Form Modal-like section */}
          {selectedService && (
            <div className="row mt-5">
              <div className="col-12">
                <h4>Apply for: {selectedService.name}</h4>
                <form onSubmit={handleSubmit}>
                  {selectedService.formSchema?.map((field) => (
                    <div className="mb-3" key={field.id}>
                      <label className="form-label">{field.label}</label>
                      {field.type === "textarea" ? (
                        <textarea
                          className="form-control"
                          name={field.id}
                          value={formData[field.id] || ""}
                          onChange={handleInputChange}
                          placeholder={field.placeholder || ""}
                          required={field.required}
                        />
                      ) : field.type === "file" ? (
                        <input
                          type="file"
                          className="form-control"
                          name={field.id}
                          onChange={handleInputChange}
                          required={field.required}
                        />
                      ) : (
                        <input
                          type={field.type}
                          className="form-control"
                          name={field.id}
                          value={formData[field.id] || ""}
                          onChange={handleInputChange}
                          placeholder={field.placeholder || ""}
                          required={field.required}
                        />
                      )}
                    </div>
                  ))}

                  <button type="submit" className="btn btn-success me-2">
                    Submit Application
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setSelectedService(null);
                      setFormData({});
                    }}
                  >
                    Cancel
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default GroupPage;